import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { displayPrice } from '../../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../../util/uiHelpers';
import { formatMoney } from '../../../util/currency';
import { ensureListing, ensureUser, userDisplayNameAsString } from '../../../util/data';
import { richText } from '../../../util/richText';
import { createSlug } from '../../../util/urlHelpers';
import { isBookingProcessAlias } from '../../../transactions/transaction';
import { learningDifficultyMetricsKeys } from '../../../config/configListing';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ReviewRating,
  IconPerson,
  IconBook,
  IconNoteBook,
  IconCalendar,
  IconMessage,
  IconInfo,
  IconCheck,
  IconCheckCircle,
} from '../../../components';

import css from './SectionProfileCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * SectionProfileCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const SectionProfileCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const { className, rootClassName, listing, renderSizes, showAuthorInfo = true } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const { title = '', description = '', publicData } = currentListing.attributes;
  const {
    teachingMetrics,
    subjects,
    ADHDExperience,
    dyslexiaExperience,
    motivationExperience,
    ratings,
    hasFreeLesson = false,
  } = publicData || {};
  const hasTeachingMetrics = !!teachingMetrics && Object.keys(teachingMetrics).length > 0;
  const hasSubjects = !!subjects && subjects.length > 0;
  const learningDifficultyMetrics = {
    ADHD: ADHDExperience,
    Dyslexia: dyslexiaExperience,
    Motivation: motivationExperience,
  };
  const hasLearningDifficultyMetrics =
    (ADHDExperience && ADHDExperience !== 'noExperience') ||
    (dyslexiaExperience && dyslexiaExperience !== 'noExperience') ||
    (motivationExperience && motivationExperience !== 'noExperience');
  const author = ensureUser(listing.author);
  const authorName = userDisplayNameAsString(author, '');
  const authorImage = author?.profileImage;
  const {
    aspectWidth = 3,
    aspectHeight = 2,
    variantPrefix = 'scaled-large',
  } = config.layout.listingImage;
  const variants = authorImage
    ? Object.keys(authorImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  let showLearningDifficultyIcon = false;
  const learningDifficultyTotalScore = (
    <span className={css.learningDifficultyTotalScore}> / 10</span>
  );
  return (
    <div className={classes}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={authorImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.mainInfo}>
          {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null}

          {hasTeachingMetrics ? (
            <div className={css.teachingMetrics}>
              <IconPerson className={css.teachingMetricsIcon} />
              <span>
                <FormattedMessage
                  id="ListingCard.teachingMetrics"
                  values={{
                    activeStudents: teachingMetrics?.activeStudents,
                    totalLessons: teachingMetrics?.totalLessons,
                  }}
                />
              </span>
            </div>
          ) : null}

          {hasSubjects ? (
            <div className={css.subjects}>
              <IconBook className={css.subjectsIcon} />
              <span>{subjects.join(', ')}</span>
            </div>
          ) : null}

          {hasLearningDifficultyMetrics ? (
            <div className={css.learningDifficultyMetrics}>
              {Object.keys(learningDifficultyMetricsKeys).map((key, index) => {
                const learningDifficulty = learningDifficultyMetrics?.[key];
                const canHelpWithLearningDifficulty =
                  key === 'ADHD' && learningDifficulty && learningDifficulty !== 'noExperience';
                const showIcon = !showLearningDifficultyIcon;
                if (showIcon) showLearningDifficultyIcon = true;
                return (
                  canHelpWithLearningDifficulty && (
                    <div className={css.learningDifficulty}>
                      <span className={css.learningDifficultyTitle}>
                        {showIcon ? (
                          <IconNoteBook className={css.learningDifficultyMetricsIcon} />
                        ) : (
                          <span className={css.spacer} />
                        )}
                        {key}
                      </span>
                      <span className={css.iconCheck}>
                        <IconCheckCircle />
                      </span>
                    </div>
                  )
                );
              })}
            </div>
          ) : null}
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          <div className={css.description}>
            {richText(description, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionProfileCard;
